import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi, onboardingApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { UserOnboardingState } from '../types';
import { isDeliverableEmailAddress } from '../utils/emailAddress';

export function EmailConfirmation({ onEdit, onComplete, onResume }: { onEdit: () => void; onComplete: () => void; onResume: (state: UserOnboardingState) => void }) {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState<'idle' | 'sent' | 'error' | 'unsupported'>('idle');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      try {
        const latest = await refreshUser();
        if (disposed) return;
        if (latest?.emailVerified) {
          // An invited company is confirmed only after the recipient's email.
          if (latest.onboarding && latest.onboarding.currentStep !== 'complete' && latest.onboarding.status !== 'verified') {
            onResume(latest.onboarding);
            return;
          }
          if (latest.onboarding?.status !== 'verified') await onboardingApi.complete();
          if (!disposed) { await refreshUser(); onComplete(); }
          return;
        }
      } catch { if (!disposed) setStatus('error'); }
      if (!disposed) timer = setTimeout(() => void check(), 5000);
    };
    void check();
    return () => { disposed = true; clearTimeout(timer); };
  }, [refreshUser, onComplete, onResume]);
  // An address saved before ClickUp 869fmnqxk may contain e.g. ö: no link was sent there.
  const unsupported = status === 'unsupported' || Boolean(user?.email && !isDeliverableEmailAddress(user.email));
  if (unsupported) return <div className="text-center">
    <h2 className="h4">{t('auth.verifyEmail.title')}</h2>
    <p className="alert alert-danger" role="alert">{t('auth.bankid.emailUnsupported', { email: user?.email || '' })}</p>
    <button type="button" className="btn btn-primary bankid-action w-100 justify-content-center" onClick={onEdit}>{t('auth.bankid.editEmail')}</button>
  </div>;
  return <div className="text-center">
    <h2 className="h4">{t('auth.verifyEmail.title')}</h2>
    <p>{t('auth.bankid.emailSent', { email: user?.email || '' })}</p>
    <p className="text-muted">{t('auth.bankid.emailExpiry')}</p>
    <p className="text-muted small">{t('auth.bankid.emailOnly')}</p>
    {status === 'sent' && <p className="alert alert-success" role="status">{t('auth.verifyEmail.resendSent')}</p>}
    {status === 'error' && <p className="alert alert-danger" role="alert">{t('common.error')}</p>}
    <button type="button" className="btn btn-primary bankid-action w-100 justify-content-center" disabled={busy} onClick={async () => {
      setBusy(true);
      try { await authApi.resendVerificationEmail(); setStatus('sent'); }
      catch (error) {
        const code = (error as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
        setStatus(code === 'EMAIL_ADDRESS_UNSUPPORTED' ? 'unsupported' : 'error');
      }
      finally { setBusy(false); }
    }}>{t('auth.verifyEmail.resend')}</button>
    <button type="button" className="btn btn-link mx-auto mt-3" onClick={onEdit}>{t('auth.bankid.editEmail')}</button>
  </div>;
}
